import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { expertAuthApi, getErrorMessage } from '../../lib/http';
import logo from '../../assets/logo.png';

// Phone validation regex (E.164 format, but flexible for user input)
const phoneRegex = /^[+]?[1-9]\d{6,14}$/;

// Validation schema for phone
const phoneSchema = z.object({
  phone: z.string()
    .min(7, 'Phone number must be at least 7 digits')
    .max(16, 'Phone number is too long')
    .refine((val) => phoneRegex.test(val.replace(/[\s\-\(\)]/g, '')), {
      message: 'Please enter a valid phone number',
    }),
});

// Validation schema for verification code
const codeSchema = z.object({
  code: z.string()
    .min(4, 'Code must be at least 4 digits')
    .max(8, 'Code is too long')
    .regex(/^\d+$/, 'Code must contain only digits'),
});

type PhoneFormData = z.infer<typeof phoneSchema>;
type CodeFormData = z.infer<typeof codeSchema>;

type Step = 'phone' | 'verify';

const ExpertCompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('phone');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [phone, setPhone] = useState<string>('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Phone form
  const phoneForm = useForm<PhoneFormData>();

  // Code form
  const codeForm = useForm<CodeFormData>();

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Step 1: Send verification code
  const onPhoneSubmit = async (data: PhoneFormData) => {
    setError('');
    setIsLoading(true);

    try {
      const normalizedPhone = data.phone.replace(/[\s\-\(\)]/g, '');
      await expertAuthApi.sendPhoneCode(normalizedPhone);
      setPhone(normalizedPhone);
      setStep('verify');
      setResendCooldown(60);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify code and complete phone
  const onCodeSubmit = async (data: CodeFormData) => {
    setError('');
    setIsLoading(true);

    try {
      await expertAuthApi.completePhone(phone, data.code);
      // Phone completed, redirect to dashboard
      navigate('/app/dashboard');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Resend code
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setError('');
    setIsLoading(true);

    try {
      await expertAuthApi.sendPhoneCode(phone);
      setResendCooldown(60);
      codeForm.reset();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col p-4">
      <div className="w-full max-w-md mx-auto flex flex-col flex-1">
        <div className="flex justify-center pt-4">
          <img src={logo} alt="BookDay" className="h-12 w-auto" />
        </div>

        <div className="flex-1" />

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Complete Your Profile</h2>
            <p className="mt-2 text-gray-600">
              Add your phone number to finish setting up your expert account
            </p>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'phone' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'
              }`}>
                {step !== 'phone' ? '✓' : '1'}
              </div>
              <div className={`w-12 h-1 ${step !== 'phone' ? 'bg-blue-600' : 'bg-gray-200'}`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'verify' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'
              }`}>
                2
              </div>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div role="alert" className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm mb-5" aria-live="polite">
              {error}
            </div>
          )}

          {/* Step 1: Phone */}
          {step === 'phone' && (
            <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-5">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  {...phoneForm.register('phone')}
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+1 234 567 8900"
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    phoneForm.formState.errors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {phoneForm.formState.errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{phoneForm.formState.errors.phone.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">We'll send you a verification code</p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Sending code...' : 'Send verification code'}
              </button>

              <button
                type="button"
                onClick={() => navigate('/app/dashboard')}
                className="w-full text-gray-500 hover:text-gray-700 text-sm py-2"
              >
                Skip for now
              </button>
            </form>
          )}

          {/* Step 2: Verify Code */}
          {step === 'verify' && (
            <form onSubmit={codeForm.handleSubmit(onCodeSubmit)} className="space-y-5">
              <div className="text-center mb-4">
                <p className="text-gray-600">Enter the code sent to</p>
                <p className="font-medium text-blue-600">{phone}</p>
              </div>

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  {...codeForm.register('code')}
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  maxLength={8}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-center text-2xl tracking-widest ${
                    codeForm.formState.errors.code ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {codeForm.formState.errors.code && (
                  <p className="mt-1 text-sm text-red-600">{codeForm.formState.errors.code.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Verifying...' : 'Verify and continue'}
              </button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isLoading}
                  className="text-blue-600 hover:underline text-sm disabled:text-gray-400 disabled:no-underline"
                >
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                </button>
                <br />
                <button
                  type="button"
                  onClick={() => { setStep('phone'); setError(''); }}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  Use a different number
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="flex-1" />

        <div className="text-center pb-4 text-sm text-gray-500">
          <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
          {' · '}
          <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
};

export default ExpertCompleteProfile;
